import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { postApi, getCurrencies } from '../services/apiServices';
import { secureStorage } from '../Utils/encryption';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { MAX_ESIM_COUNT } from '../Utils/constants';
import { CartIcon, CloseIcon, TrashIcon } from './r4l/icons';


const parseNumericString = (str) => {
  if (!str) return 0;
  return parseFloat(str.toString().replace(/,/g, '')) || 0;
};

// Format travel dates like "15 Dec - 30 Dec" (adds year if different)
const formatDateRange = (start, end, locale = 'en-US') => {
  if (!start) return '';

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  const isValid = (date) => date instanceof Date && !isNaN(date);

  if (!isValid(startDate)) return '';

  const baseOpts = { day: 'numeric', month: 'short' };
  const format = (date, opts = baseOpts) => new Intl.DateTimeFormat(locale || 'en-US', opts).format(date);

  if (endDate && isValid(endDate)) {
    const opts = startDate.getFullYear() !== endDate.getFullYear()
      ? { ...baseOpts, year: 'numeric' }
      : baseOpts;
    return `${format(startDate, opts)} - ${format(endDate, opts)}`;
  }

  return format(startDate);
};

// Language mapping function to convert app language codes to API language codes
const getApiLanguageCode = (appLanguage) => {
  const languageMap = {
    'ar': 'ar',     // Arabic
    'da': 'da',     // Danish
    'de': 'de',     // German
    'en': 'en',     // English
    'es': 'es',     // Spanish
    'fr': 'fr',     // French
    'he': 'he',     // Hebrew
    'it': 'it',     // Italian
    'ja': 'ja',     // Japanese
    'ko': 'ko',     // Korean
    'nl': 'nl',     // Dutch
    'no': 'no',     // Norwegian
    'pl': 'pl',     // Polish
    'pt': 'pt',     // Portuguese
    'ru': 'ru',     // Russian
    'sv': 'sv',     // Swedish
    'tr': 'tr',     // Turkish
    'zh': 'zh_CN'   // Chinese (Simplified)
  };
  return languageMap[appLanguage] || 'en'; // Default to English if not found
};

function CartDropdown() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [currencies, setCurrencies] = useState([]);
  const [apiCalculatedTotal, setApiCalculatedTotal] = useState(0);
  const [isCalculatingTotal, setIsCalculatingTotal] = useState(false);
  const calculationTimeoutRef = useRef(null);
  const isCalculatingRef = useRef(false);

  
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('cart-open');
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.classList.remove('cart-open');
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.classList.remove('cart-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const loadCartItems = () => {
      const savedItems = secureStorage.getItem('checkoutSession') || [];
   
      
      setCartItems(savedItems);
      setCartCount(savedItems.length);
      
      const storedCurrency = localStorage.getItem('selectedCurrency');
      if (storedCurrency) {
        setSelectedCurrency(storedCurrency);
      }

      if (savedItems.length > 0) {
        const currency = storedCurrency || selectedCurrency;
      
        if (currency && !isCalculatingRef.current) {
          calculateCartTotal(savedItems, currency);
        }
      } else {
        setApiCalculatedTotal(0);
      }
    };

    loadCartItems();

    const handleStorageChange = () => {
      loadCartItems();
    };

    window.addEventListener('storage', handleStorageChange);
    
    window.addEventListener('cartUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleStorageChange);
    };
  }, []); 

  useEffect(() => {
    // The drawer closes on backdrop click (see render) or Escape.
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const storedCurrency = localStorage.getItem('selectedCurrency');
    if (storedCurrency) {
      setSelectedCurrency(storedCurrency);
    }
  }, []);

  useEffect(() => {
    if (cartItems.length > 0 && selectedCurrency && !isCalculatingRef.current) {
    
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
      
      
      calculationTimeoutRef.current = setTimeout(() => {
        calculateCartTotal(cartItems, selectedCurrency);
      }, 300);
    }
    
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [selectedCurrency, cartItems.length]); 

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await getCurrencies();
        if (response?.status && Array.isArray(response.data)) {
          setCurrencies(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch currencies:', err);
      }
    };
    fetchCurrencies();
  }, []);

  const toggleCart = () => setIsOpen(!isOpen);
  const closeCart = () => setIsOpen(false);

  const calculateCartTotal = async (items = cartItems, currency = selectedCurrency) => {
   
    
    if (items.length === 0) {
     
      setApiCalculatedTotal(0);
      return;
    }

    if (isCalculatingRef.current) {
     
      return;
    }

    isCalculatingRef.current = true;
    setIsCalculatingTotal(true);
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('API timeout')), 5000);
    });

    try {
    
      const apiItems = items.map(item => {
        const mappedItem = {
          type: item.plan_type === 'Data Bundles' ? 'bundle' : 
                item.plan_type === 'Global eSIM' ? 'global' : 'unlimited',
          country: item.country_code || item.iso3 || 'USA',
          days: item.days || 1,
          no_of_esims: item.esim_count || 1,
          data_usage_allowance: item.gb || 20
        };

        return mappedItem;
      });

      const payload = {
        items: apiItems,
        currency: currency,
        lang: getApiLanguageCode(currentLanguage)
      };

      
      const response = await Promise.race([
        postApi('/api/calculate-cart', payload),
        timeoutPromise
      ]);

      if (response?.status && response.data?.total) {
        const total = parseNumericString(response.data.total);
        setApiCalculatedTotal(total);
       
        
        if (response.data.items && response.data.items.length > 0) {
          const updatedItems = [...items]; 
          response.data.items.forEach((apiItem, index) => {
            if (updatedItems[index]) {
              updatedItems[index].api_calculated_price = parseNumericString(apiItem.amount);
            }
          });
          setCartItems(updatedItems);
          secureStorage.setItem('checkoutSession', updatedItems);
        }
      } else {
        console.error('Failed to calculate cart total:', response?.message);
   
        setApiCalculatedTotal(getFallbackTotalPrice(items));
      }
    } catch (error) {
      console.error('Error calculating cart total:', error);
     
      setApiCalculatedTotal(getFallbackTotalPrice(items));
    } finally {
      setIsCalculatingTotal(false);
      isCalculatingRef.current = false;

    }
  };

  
  const getFallbackTotalPrice = (items = cartItems) => {
    const total = items.reduce((total, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      return total + itemPrice;
    }, 0);
    return Math.round(total * 100) / 100;
  };

 
  const getTotalPrice = () => {
    return apiCalculatedTotal;
  };

  const handleQuantityChange = async (index, newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= MAX_ESIM_COUNT) {
      const updatedItems = [...cartItems];
      const item = updatedItems[index];
      
     
      item.esim_count = newQuantity;
      
      setCartItems(updatedItems);
      setCartCount(updatedItems.length);
      secureStorage.setItem('checkoutSession', updatedItems);
      
      await calculateCartTotal(updatedItems, selectedCurrency);
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const increaseQuantity = async (index) => {
    const updatedItems = [...cartItems];
    const item = updatedItems[index];
    const currentQuantity = item.esim_count || 1;
    
    if (currentQuantity >= MAX_ESIM_COUNT) {
      return; // Don't increase if already at max
    }
    
    const newQuantity = currentQuantity + 1;
    
    item.esim_count = newQuantity;
    
    setCartItems(updatedItems);
    secureStorage.setItem('checkoutSession', updatedItems);
    
    await calculateCartTotal(updatedItems, selectedCurrency);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const decreaseQuantity = async (index) => {
    const updatedItems = [...cartItems];
    const item = updatedItems[index];
    const currentQuantity = item.esim_count || 1;
    
    if (currentQuantity > 1) {
      const newQuantity = currentQuantity - 1;
      
      item.esim_count = newQuantity;
      
      setCartItems(updatedItems);
      secureStorage.setItem('checkoutSession', updatedItems);
      
      await calculateCartTotal(updatedItems, selectedCurrency);
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const deleteItem = async (index) => {
    const updatedItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedItems);
    setCartCount(updatedItems.length);
    secureStorage.setItem('checkoutSession', updatedItems);
    
    await calculateCartTotal(updatedItems, selectedCurrency);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  return (
    <div className="cart-container">
      <a className="nav-link dropdown-toggle cart" href="#" role="button" aria-label="Cart"
        onClick={(e) => {
          e.preventDefault();
          toggleCart();
        }}
        aria-expanded={isOpen}>
        <span>
          <CartIcon />
          {cartCount > 0 && (
            <span className="cart-count">{cartCount}</span>
          )}
        </span>
      </a>
    
      {createPortal(
        <>
          <div className={`r4l-drawer-bg${isOpen ? ' is-open' : ''}`} onClick={closeCart} aria-hidden="true" />
          <aside
            className={`r4l-drawer${isOpen ? ' is-open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={t('cartDropdown.title')}
            aria-hidden={!isOpen}
          >
            <header className="r4l-drawer-head">
              <h2>{t('cartDropdown.title')}</h2>
              <button type="button" className="r4l-icon-btn" onClick={closeCart} aria-label="Close">
                <CloseIcon />
              </button>
            </header>

            <div className="r4l-drawer-body">
              {isCalculatingTotal && (
                <div className="r4l-drawer-loading"><span className="loader"></span></div>
              )}
              {cartItems.length > 0 ? (
                <ul className="r4l-cart-list">
                  {cartItems.map((item, index) => {
                    const itemCurrency = item.currency || selectedCurrency;
                    const itemSymbol = currencies.find(c => c.code === itemCurrency)?.symbol || '$';
                    return (
                      <li key={index} className="r4l-cart-item">
                        <img
                          className="r4l-cart-flag"
                          src={item.flag || window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg')}
                          alt={`${item.country} eSIM`}
                          onError={(e) => {
                            e.target.src = window.getPublicUrl('images/earth-globe-global-svgrepo-com (1).svg');
                          }}
                        />
                        <div className="r4l-cart-info">
                          <b>{item.country}</b>
                          <span>{t('cartDropdown.eSIM')} · {item.plan_type === 'Global eSIM' ? t('destinationDetail.tabs.globalESIM') : item.plan_type}</span>
                          <span>
                            {item.days} {t('cartDropdown.days')}
                            {item.gb ? ` · ${item.gb} ${t('cartDropdown.gb')}` : ''}
                          </span>
                          {item.startDate && (
                            <span>{formatDateRange(item.startDate, item.endDate, currentLanguage)}</span>
                          )}
                          <div className="r4l-cart-qty">
                            <button type="button" onClick={() => decreaseQuantity(index)} disabled={isCalculatingTotal} aria-label="Decrease">−</button>
                            <input
                              type="text"
                              value={item.esim_count || 1}
                              onChange={(e) => handleQuantityChange(index, Number(e.target.value) || 1)}
                              disabled={isCalculatingTotal}
                              aria-label={t('cartDropdown.eSIM')}
                            />
                            <button type="button" onClick={() => increaseQuantity(index)} disabled={isCalculatingTotal} aria-label="Increase">+</button>
                          </div>
                        </div>
                        <div className="r4l-cart-side">
                          <b>{itemSymbol}{(parseFloat(item.api_calculated_price || item.price) || 0).toFixed(2)}</b>
                          <small>{itemCurrency}</small>
                          <button
                            type="button"
                            className="r4l-cart-remove"
                            onClick={() => deleteItem(index)}
                            disabled={isCalculatingTotal}
                            aria-label="Delete"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="r4l-cart-empty">
                  <span><CartIcon /></span>
                  <p>{t('cartDropdown.emptyCart')}</p>
                </div>
              )}
            </div>

            <footer className="r4l-drawer-foot">
              {cartItems.length > 0 && (
                <>
                  <div className="r4l-cart-total">
                    <span>{t('cartDropdown.total')}</span>
                    {isCalculatingTotal ? (
                      <span className="r4l-cart-calculating">{t('cartDropdown.calculating')}</span>
                    ) : (
                      <b>
                        {currencies.find(c => c.code === selectedCurrency)?.symbol || '$'}{getTotalPrice().toFixed(2)} <small>{selectedCurrency}</small>
                      </b>
                    )}
                  </div>
                  <Link
                    to="/checkout"
                    className={`r4l-btn${isCalculatingTotal ? ' is-disabled' : ''}`}
                    aria-disabled={isCalculatingTotal}
                    onClick={(e) => { if (isCalculatingTotal) e.preventDefault(); }}
                  >
                    {isCalculatingTotal ? t('cartDropdown.calculating') : t('cartDropdown.goToCheckout')}
                  </Link>
                </>
              )}
              <button type="button" className="r4l-btn-line" onClick={closeCart} disabled={isCalculatingTotal}>
                {t('cartDropdown.continueShopping')}
              </button>
            </footer>
          </aside>
        </>,
        document.body
      )}
    </div>
  );
};

export default CartDropdown;